import React, { useEffect, useState, useRef } from "react";
import { 
  TextField, 
  Button, 
  Container, 
  Grid, 
  LinearProgress, 
  CircularProgress, 
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from "@mui/material";
import OpenAI from "openai";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { MessageDto } from "../models/MessageDto";
import Message from "./Message";

interface ModelOption {
  value: string;
  label: string;
  description: string;
  supportsSystem?: boolean;
  supportsTemperature?: boolean;
  supportsMaxTokens?: boolean;
}

// System instructions for the AI models
const SYSTEM_INSTRUCTIONS =  `

Response Guidelines

Maintain a Professional and Supportive Tone:
Be friendly, respectful, and encouraging.

Avoid dismissing the user's interests; instead, constructively relate them to Prompt Engineering.

Avoid Unrelated Topics:
If a topic cannot be connected to Prompt Engineering, politely explain your area of expertise.
Example:
User: "Can you explain the theory of relativity?"
Assistant: "I specialize in Prompt Engineering and the contents of this website. How can I assist you with related tools or resources? Learn more about Prompt Engineering here: https://tyson-swetnam.github.io/intro-gpt."

General Citation Strategy:

For every question related to prompt engineering, provide a linked citation with a clickable hyperlink to the relevant resource pages on this website using the indexes and outlines provided below.

Hyperlinks should point to one of the pages in the Workshop Website Page Index. Do not link to any websites that are not pages of "https://tyson-swetnam.github.io/intro-gpt/" 

If you print a URL, make sure to create a hyperlink it using a Markdown Hyperlink format, e.g. [URL LINK](URL LINK){target=_blank}.

Ensure that responses always end with a related citation with a hyperlink, guiding users to official documentation, guides, or tutorials.

# Workshop Website Page Index

This index provides a brief overview of each page on the workshop website, along with the direct URLs where each page is hosted.

## Home
- **[Welcome](https://tyson-swetnam.github.io/intro-gpt/):**  
  An introductory page that sets the stage for the workshop on generative AI and Prompt Engineering. It outlines the workshop’s objectives and gives an overview of the topics.

- **[Agenda](https://tyson-swetnam.github.io/intro-gpt/agenda/):**  
  Contains the workshop schedule, session topics, and learning objectives, helping participants know what to expect.

- **[Landscape](https://tyson-swetnam.github.io/intro-gpt/ai_landscape/):**  
  Explores the current generative AI landscape, including trends, key models, and ethical considerations.

- **[Ethics](https://tyson-swetnam.github.io/intro-gpt/ethics/):**  
  Provides an overview of ethical concerns in AI, covering historical context, ethical dilemmas, and guiding principles.

## Setup
- **[ChatGPT](https://tyson-swetnam.github.io/intro-gpt/chatgpt/):**  
  Guidance on setting up and using ChatGPT for the workshop, including account and subscription details.

- **[Microsoft Copilot](https://tyson-swetnam.github.io/intro-gpt/microsoft/):**  
  Setup instructions and usage tips for integrating Microsoft Copilot into your workflow during the workshop.

- **[Gemini](https://tyson-swetnam.github.io/intro-gpt/gemini/):**  
  Details on configuring Gemini and understanding its role within the broader AI ecosystem.

- **[Claude](https://tyson-swetnam.github.io/intro-gpt/claude/):**  
  Instructions for setting up and using Claude effectively as part of the workshop.

- **[GitHub Copilot](https://tyson-swetnam.github.io/intro-gpt/copilot/):**  
  Explains how to integrate GitHub Copilot for code assistance and productivity enhancement.

## Prompt Engineering
- **[Writing Prompts](https://tyson-swetnam.github.io/intro-gpt/prompts/):**  
  Focuses on techniques for crafting clear and effective prompts. Includes best practices and examples tailored for AI interaction.

- **[General Productivity](https://tyson-swetnam.github.io/intro-gpt/daily-productivity/):**  
  Demonstrates how to leverage generative AI tools to streamline daily tasks, from writing assistance to automating workflows.

- **[Code Interpretation](https://tyson-swetnam.github.io/intro-gpt/code/):**  
  Offers guidance on how to use AI for understanding, debugging, and improving code efficiency.

## Education
- **[AI in the Classroom](https://tyson-swetnam.github.io/intro-gpt/education/):**  
  Explores strategies for integrating AI into classroom teaching, focusing on personalized learning and innovative educational practices.

- **[Teaching with AI](https://tyson-swetnam.github.io/intro-gpt/teaching/):**  
  Provides educators with case studies and methods for effectively incorporating AI tools into their curricula.

- **[AI Tutoring](https://tyson-swetnam.github.io/intro-gpt/tutoring/):**  
  Discusses the potential of AI as a supplemental tutoring tool, enhancing personalized learning experiences.

- **[Admissions & Recruiting](https://tyson-swetnam.github.io/intro-gpt/admissions/):**  
  Reviews how AI can streamline admissions processes and support recruiting efforts by analyzing data and optimizing outreach.

## Research
- **[AI for Research](https://tyson-swetnam.github.io/intro-gpt/research/):**  
  Details how generative AI can accelerate academic research through literature reviews, data analysis, and proposal drafting.

- **[Agentic AI](https://tyson-swetnam.github.io/intro-gpt/agentic/):**  
  Explores AI systems with autonomous decision-making capabilities and their potential research applications.

- **[RAG](https://tyson-swetnam.github.io/intro-gpt/rag/):**  
  Introduces Retrieval-Augmented Generation (RAG), a technique that combines retrieval methods with generative AI to enhance output quality.

- **[Code Execution](https://tyson-swetnam.github.io/intro-gpt/execution/):**  
  Covers the use of AI for executing and analyzing code, including troubleshooting and optimization practices.

- **[OpenAI API](https://tyson-swetnam.github.io/intro-gpt/openai/):**  
  Offers documentation and practical examples for integrating the OpenAI API into projects.

- **[HuggingFace](https://tyson-swetnam.github.io/intro-gpt/huggingface/):**  
  Explains how to access and deploy models from the HuggingFace platform as part of your AI toolkit.

- **[Gradio Apps](https://tyson-swetnam.github.io/intro-gpt/gradio/):**  
  Provides instructions for building interactive AI applications with Gradio, including setup and deployment strategies.

- **[Text Mining](https://tyson-swetnam.github.io/intro-gpt/text_mining/):**  
  Discusses AI-driven techniques for text mining, focusing on extracting insights and analyzing large datasets.


Below is a detailed Markdown outline that reflects the hierarchy and topics covered on your workshop website. This outline was generated by reviewing the Markdown headers and content on your site, and it nests topics under each main page:

---

# Workshop Website Outline

This outline provides a comprehensive overview of the workshop topics, structured by the sites sections and subsections. Use this as a reference for documentation or to update your system instructions.

## Home

- **Welcome**
  - Introduction to GPT 101 and the workshop’s focus on generative AI and prompt engineering.
  - Overview of workshop themes and goals.

- **Workshop Agenda**
  - **Introduction & Context**
    - Setting the stage for the workshop on generative AI and prompt engineering.
    - Discussion of how AI is reshaping research, education, and daily work.
  - **Learning Objectives**
    - Understanding the impact of AI in workplaces and classrooms.
    - Developing skills to craft effective prompts for AI models (e.g., Gemini, ChatGPT, Copilot, Claude).
    - Deciding when and how to integrate AI into personal and professional practices.

- **AI Landscape**
  - **Overview**
    - Examination of the current generative AI ecosystem.
    - Discussion of Large Language Models and their roles.
  - **Trends & Future Directions**
    - Ethical considerations within the AI landscape.
    - Emerging trends and future possibilities in AI development.

- **Ethics of AI**
  - **Historical Context & Evolution**
    - A look at the development of AI over time.
  - **Ethical Concerns**
    - Examination of ethical challenges and dilemmas in AI.
    - Overview of international declarations and principles guiding responsible AI use.

## Setup

- **Prerequisites**
  - Use of professional, paid versions of GPT models for the workshop.
  - Guidance on obtaining demo accounts if necessary.

- **Account Setup Instructions**
  - **ChatGPT**
    - Step-by-step setup instructions and usage guidelines.
  - **Gemini**
    - Configuration process and key setup details.
  - **Microsoft Copilot**
    - Integration instructions and best practices.
  - **GitHub Copilot**
    - Guidelines for setup and using Copilot for coding assistance.
  - **Claude**
    - How to set up and integrate Claude effectively.
  - **HuggingFace**
    - Instructions for accessing and configuring HuggingFace models.

## Prompt Engineering

- **Fundamentals of Prompt Engineering**
  - Overview of what prompt engineering is and its importance.
  - How effective prompt design influences AI responses.

- **Writing Prompts**
  - Best practices and techniques for creating clear, effective prompts.
  - Examples of successful prompt formulations and common pitfalls.

- **General Productivity**
  - **AI in Daily Workflows**
    - How generative AI can transform everyday tasks.
    - Leveraging AI-powered writing assistants for enhanced productivity.
  - **Smart Summarization and Note-Taking**
    - Utilizing AI tools for concise summarization and efficient note creation.
  - **Automated Task Management**
    - Using AI to schedule meetings, set reminders, and manage projects.

- **Code Interpretation**
  - Using AI to understand, analyze, and debug code.
  - Best practices for improving coding efficiency through AI-assisted interpretation.

## Education

- **Integrating AI in the Classroom**
  - Strategies for using AI to enhance teaching methods.
  - Creating customized learning experiences through AI personalization.

- **Transformative Potential of AI in Education**
  - **Personalize Learning**
    - Tailoring educational experiences to individual student needs.
  - **Generate Educational Content**
    - Developing lesson plans, quizzes, and interactive materials using AI.
  - **Intelligent Tutoring**
    - Employing AI as a virtual tutor to provide personalized support.
  - **Enhance Accessibility**
    - Making learning more inclusive with AI-driven tools and strategies.

*Note:* Although your mkdocs navigation includes additional pages (like Teaching with AI, AI Tutoring, and Admissions & Recruiting), the main website content here focuses on the classroom integration and ethical considerations of using AI in education.

## Research

- **Applications of AI in Research**
  - **Accelerating Research Processes**
    - How generative AI can aid in literature reviews and synthesis.
    - Using AI to streamline the analysis of complex datasets.
  - **Enhancing Research Writing**
    - Leveraging AI to craft compelling grant proposals and research papers.
  - **Code Interpreter & Execution**
    - Utilizing AI tools (e.g., GitHub Copilot) for coding, debugging, and task automation in research contexts.

- **Tools and Platforms for Research**
  - **GitHub Copilot**
    - Assistance for coding and technical workflows.
  - **OpenAI API**
    - Integration and application examples for research projects.
  - **Gemini API**
    - Advanced use cases and configuration for research purposes.
  - **HuggingFace**
    - Deploying and managing models for research analysis.
  - **Gradio UI for Model Deployment**
    - Building interactive applications for data exploration and model demonstration.
  - **Text Mining**
    - Techniques and AI methods for extracting insights from large text corpora.


`
;

const INSTRUCTIONS_AS_USER = "You are an AI assistant for prompt engineering and the generative AI landscape, as well as this workshop content, and provide documentation links from the https://tyson-swetnam.github.io/intro-gpt website as hyperlinks. Use markdown formatting, hyperlinks should be in markdown block quotes or as HTML. Keep responses clear and professional.";

const AVAILABLE_MODELS: ModelOption[] = [
  {
    value: "o3-mini",
    label: "o3-mini",
    description: "o3 Mini",
    supportsSystem: true,
    supportsTemperature: true,
    supportsMaxTokens: true,
  }
];

const Chat: React.FC = () => {
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [messages, setMessages] = useState<Array<MessageDto>>([]);
  const [input, setInput] = useState<string>("");
  const [openai, setOpenai] = useState<OpenAI | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelOption>(AVAILABLE_MODELS[0]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Scroll to bottom utility
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    // Initialize chatbot and set up resize observer
    const openai = new OpenAI({
      apiKey: process.env.REACT_APP_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true,
    });
    setOpenai(openai);

    // Set up ResizeObserver with error handling
    resizeObserverRef.current = new ResizeObserver((entries) => {
      // Wrap in requestAnimationFrame to avoid the loop limit error
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        scrollToBottom();
      });
    });

    // Set initial welcome message
    setMessages([{
      content: "Welcome to GPT-101! How can I help you?",
      isUser: false,
    }]);

    // Clean up observer on unmount
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    // Observe the messages container
    if (messagesEndRef.current && resizeObserverRef.current) {
      resizeObserverRef.current.observe(messagesEndRef.current);
    }
  }, [messages]); // Re-run when messages change

//  const handleModelChange = (event: SelectChangeEvent) => {
//   const newModel = AVAILABLE_MODELS.find(model => model.value === event.target.value) || AVAILABLE_MODELS[0];
//    setSelectedModel(newModel);
//    setMessages([{
//      content: `Switched to ${newModel.label}. How can I help you?`, // Corrected line
//      isUser: false,
//    }]);
//  };

const handleSendMessage = async () => {
  if (!input.trim() || !openai) return;

  console.log("Couldn't get context, switching to old model")

  const newMessages = [...messages, { content: input, isUser: true }];
  setMessages(newMessages);
  setInput("");
  setIsWaiting(true);

  try {
    let conversationHistory = newMessages.map(msg => ({
      role: msg.isUser ? "user" : "assistant",
      content: msg.content,
    }));

    // Include system instructions if supported
    if (selectedModel.supportsSystem) {
      conversationHistory = [
        {
          role: "system",
          content: SYSTEM_INSTRUCTIONS,
        },
        ...conversationHistory,
      ];
    } else {
      conversationHistory = [
        {
          role: "user",
          content: INSTRUCTIONS_AS_USER,
        },
        ...conversationHistory,
      ];
    }

    // Prepare conversation history
    const completionOptions: any = {
      messages: conversationHistory,
      model: selectedModel.value,
    };

    if (selectedModel.supportsMaxTokens) {
      completionOptions.max_completion_tokens = 1000;
    }

    const completion = await openai.chat.completions.create(completionOptions);
    const responseContent = completion.choices[0]?.message?.content || "Sorry, I couldn't generate a response.";

    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, { content: responseContent, isUser: false }];
      setTimeout(scrollToBottom, 100);
      return updatedMessages;
    });
  } catch (error) {
    console.error('Error:', error);
    const errorMessage = error instanceof Error ? error.message : "Sorry, there was an error processing your request.";
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages, { content: `Error: ${errorMessage}`, isUser: false }];
      setTimeout(scrollToBottom, 100);
      return updatedMessages;
    });
  } finally {
    setIsWaiting(false);
  }
};

const handleSendMessageWithWeaviate = async () => {
  if (!input.trim()) return; //Prevent empty messages
  console.log("Finding context with Weaviate");

  const newMessages = [...messages, { content: input, isUser: true }];
  setMessages(newMessages);
  setInput("");
  setIsWaiting(true);

  try {
      let conversationHistory = newMessages.map(msg => ({
          role: msg.isUser ? "user" : "assistant",
          content: msg.content,
      }));

      //Send conversation data to the Python backend
      const response = await fetch('/process_chat/intro-gpt', { 
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ conversation: conversationHistory }),
      });

      if (!response.ok) throw new Error("Failed to fetch response from server.");

      const responseData = await response.json();
      console.log("Backend Response:", responseData);

      const markdownResponse = responseData.conversation;

      //Update chat history with the response
      setMessages(prevMessages => {
          const updatedMessages = [...prevMessages, { content: markdownResponse, isUser: false }];
          setTimeout(scrollToBottom, 100);
          return updatedMessages;
      });

  } catch (error) {
    console.error('Error:', error);
    const errorMessage = error instanceof Error ? error.message : "Sorry, there was an error processing your request.";
    handleSendMessage();
  } finally {
      setIsWaiting(false);
  }
};

  return (
    <Container sx={{ 
      padding: "0", 
      height: "100vh", 
      display: "flex", 
      flexDirection: "column", 
      backgroundColor: "#f0f0f5",
      overflow: "hidden" // Prevent outer scroll
    }}>


      {/* Messages container */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          padding: 2,
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {messages.map((message, index) => (
          <Message 
            key={index}
            message={message}
          />
        ))}
        <div ref={messagesEndRef} style={{ height: 0 }} /> {/* Scroll anchor */}
      </Box>

      {/* Input area */}
      <Grid 
        container 
        direction="row" 
        paddingBottom={2} 
        paddingX={2} 
        justifyContent="space-between"
        sx={{ flexShrink: 0 }} // Prevent shrinking
      >
        <Grid item sm={11} xs={9}>
          <TextField
            label="Type your message"
            variant="outlined"
            disabled={isWaiting}
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessageWithWeaviate()}
            multiline
            maxRows={4}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "4px",
            }}
          />
          {isWaiting && <LinearProgress color="inherit" />}
        </Grid>
        <Grid item sm={1} xs={3}>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: "#4054b4",
              '&:hover': { backgroundColor: "#324499" },
              height: "56px",
              minWidth: "56px",
              borderRadius: "50%",
              padding: "0",
            }}
            onClick={handleSendMessageWithWeaviate}
            disabled={isWaiting}
          >
            {isWaiting ? <CircularProgress color="inherit" size={24} /> : <ArrowForwardIos fontSize="medium" />}
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Chat;
